import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

// Small helpers
export function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function addDaysISO(iso, delta) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

export function monthKey(iso) {
  return iso.slice(0, 7);
}

export function formatLongDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function monthLabel(monthKeyStr) {
  const [y, m] = monthKeyStr.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export function daysInMonth(monthKeyStr) {
  const [y, m] = monthKeyStr.split("-").map(Number);
  return new Date(y, m, 0).getDate();
}

// Fetch today's payload once (used by Dashboard + gates)
export function useToday() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/calendar/today");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

// Fetch a month's plan (all recipes/movements/coaching)
export function useMonth(monthKeyStr) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    if (!monthKeyStr) return;
    setLoading(true);
    try {
      const r = await api.get(`/calendar/month/${monthKeyStr}`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [monthKeyStr]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

// Progress by date+kind (kind: meal | movement | coaching)
export function useProgress() {
  const [keys, setKeys] = useState(new Set());
  const load = useCallback(async () => {
    const { data } = await api.get("/progress");
    setKeys(new Set(data.keys));
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (date, kind) => {
    const { data } = await api.post("/progress/toggle", { date, kind });
    setKeys((prev) => {
      const next = new Set(prev);
      if (data.done) next.add(data.key); else next.delete(data.key);
      return next;
    });
  };
  const has = (date, kind) => keys.has(`${date}-${kind}`);
  return { keys, has, toggle };
}
